import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatDate, formatFullDateTime12h } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ScrollArea } from '../../components/ui/scroll-area';
import { FileText, CheckCircle, AlertTriangle, Shield, Building, Eye } from 'lucide-react';
import { toast } from 'sonner';

const ClientAgreement = () => {
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [acceptedAgreement, setAcceptedAgreement] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agreementRes, statusRes] = await Promise.all([
        api.get('/agreement/current?agreement_type=client'),
        api.get('/agreement/status')
      ]);
      setAgreement(agreementRes.data);
      setStatus(statusRes.data);
      
      if (statusRes.data.has_accepted) {
        setHasAccepted(true);
        setHasRead(true);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to load agreement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewAccepted = async () => {
    try {
      const res = await api.get('/agreement/view-accepted');
      setAcceptedAgreement(res.data);
      setViewMode(true);
    } catch (error) {
      toast.error('Failed to load accepted agreement');
    }
  };

  const handleAccept = async () => {
    if (!hasRead || !hasAccepted) {
      toast.error('Please read and accept the agreement to continue');
      return;
    }

    setAccepting(true);
    try {
      await api.post('/agreement/accept', { accepted: true, agreement_type: 'client' });
      toast.success('Agreement accepted successfully!');
      
      setTimeout(() => {
        navigate('/client/dashboard');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept agreement');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // View mode - showing the accepted agreement
  if (viewMode && acceptedAgreement) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="view-agreement-page">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <FileText className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {acceptedAgreement.agreement_title}
                  </h1>
                  <p className="text-gray-500">
                    Accepted on {formatDate(acceptedAgreement.accepted_at)}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline"
                onClick={() => setViewMode(false)}
              >
                Back
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              <ScrollArea className="h-[600px] p-6">
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {acceptedAgreement.full_text}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If already accepted, show confirmation with view option
  if (status?.has_accepted) {
    return (
      <div className="min-h-screen bg-gray-50 py-8" data-testid="agreement-accepted-page">
        <div className="max-w-3xl mx-auto px-4">
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-800 mb-2">Agreement Accepted</h2>
              <p className="text-green-700 mb-6">
                You accepted the Client and Agency Workspace Agreement on{' '}
                {status.accepted_at ? formatFullDateTime12h(status.accepted_at) : 'record date'}
              </p>
              <div className="flex gap-4 justify-center">
                <Button 
                  variant="outline"
                  onClick={handleViewAccepted}
                  className="border-green-600 text-green-700 hover:bg-green-100"
                  data-testid="view-agreement-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Agreement
                </Button>
                <Button 
                  onClick={() => navigate('/client/dashboard')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-agreement-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Building className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {agreement?.title || 'Client and Agency Workspace Agreement'}
              </h1>
              <p className="text-gray-500">
                Please read and accept the agreement to continue
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Important Notice */}
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-700">
            <strong>Important:</strong> You must read and accept this agreement before you can post gigs, 
            book talent, or manage events on the iEvntür platform.
          </AlertDescription>
        </Alert>

        {/* Agreement Card */}
        <Card className="mb-6 bg-white border-gray-200">
          <CardHeader className="border-b border-gray-100 bg-gray-50">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              {agreement?.title || 'Client and Agency Workspace Agreement'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px] p-6">
              <div 
                className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap"
                data-testid="agreement-text"
              >
                {agreement?.full_text || 'Loading agreement...'}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Acceptance Section */}
        <Card className="bg-white border-gray-200">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Checkbox 1: I have read */}
              <div 
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                onClick={() => setHasRead(!hasRead)}
              >
                <Checkbox
                  id="hasRead"
                  checked={hasRead}
                  onCheckedChange={setHasRead}
                  className="mt-0.5"
                  data-testid="read-checkbox"
                />
                <label htmlFor="hasRead" className="text-sm text-gray-700 cursor-pointer">
                  I have read and understand the Client and Agency Workspace Agreement in its entirety.
                </label>
              </div>

              {/* Checkbox 2: I accept */}
              <div 
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                  hasRead 
                    ? 'border-gray-200 hover:bg-gray-50' 
                    : 'border-gray-100 bg-gray-50 opacity-60'
                }`}
                onClick={() => hasRead && setHasAccepted(!hasAccepted)}
              >
                <Checkbox
                  id="hasAccepted"
                  checked={hasAccepted}
                  onCheckedChange={setHasAccepted}
                  disabled={!hasRead}
                  className="mt-0.5"
                  data-testid="accept-checkbox"
                />
                <label htmlFor="hasAccepted" className={`text-sm cursor-pointer ${hasRead ? 'text-gray-700' : 'text-gray-400'}`}>
                  I agree to the terms of the Client and Agency Workspace Agreement. I understand that by selecting 
                  "Accept & Continue," I am providing my legally binding electronic signature under the ESIGN Act and UETA.
                </label>
              </div>

              {/* Legal Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p className="font-medium mb-1">Electronic Signature Notice</p>
                <p>
                  By clicking "Accept & Continue," you are signing this agreement electronically. 
                  This electronic signature is legally binding and has the same legal effect as a handwritten signature.
                  Your acceptance will be recorded with a timestamp and your device information.
                </p>
              </div>

              {/* Accept Button */}
              <div className="pt-4 flex justify-end">
                <Button
                  onClick={handleAccept}
                  disabled={!hasRead || !hasAccepted || accepting}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8"
                  data-testid="accept-button"
                >
                  {accepting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept & Continue
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientAgreement;
